import type {AppError, BusinessErrorCodes} from '@/domain/interfaces/ErrorService';
import type {HttpClient} from '@/infrastructure/interfaces/HttpClient';

export class JsonHttpClient implements HttpClient {
  constructor(
    private readonly baseUrl: string,
    private readonly getToken?: () => Promise<string | undefined>,
  ) {}

  async delete(path: string): Promise<void> {
    const headers: {[p: string]: string} = {};
    if (this.getToken) {
      headers.Authorization = `Bearer ${await this.getToken()}`;
    }
    return fetchJson({method: 'DELETE', baseUrl: this.baseUrl, path, headers});
  }

  async get<Response>(path: string): Promise<Response> {
    const headers: {[p: string]: string} = {};
    if (this.getToken) {
      headers.Authorization = `Bearer ${await this.getToken()}`;
    }
    return fetchJson({method: 'GET', baseUrl: this.baseUrl, path, headers});
  }

  async patch<Request, Response>(path: string, body: Partial<Request>): Promise<Response> {
    const headers: {[p: string]: string} = {};
    if (this.getToken) {
      headers.Authorization = `Bearer ${await this.getToken()}`;
    }
    return fetchJson({method: 'PATCH', body: body, baseUrl: this.baseUrl, path, headers});
  }

  async post<Request, Response>(path: string, body: Request): Promise<Response> {
    const headers: {[p: string]: string} = {};
    if (this.getToken) {
      headers.Authorization = `Bearer ${await this.getToken()}`;
    }
    return fetchJson({method: 'POST', body: body, baseUrl: this.baseUrl, path, headers});
  }

  async put<Request, Response>(path: string, body: Request): Promise<Response> {
    const headers: {[p: string]: string} = {};
    if (this.getToken) {
      headers.Authorization = `Bearer ${await this.getToken()}`;
    }
    return fetchJson({method: 'PUT', body: body, baseUrl: this.baseUrl, path, headers});
  }
}

export class HttpError implements AppError {
  constructor(
    public statusCode: number,
    public message: string,
    // biome-ignore lint/suspicious/noExplicitAny: "
    public payload?: any,
    public businessErrorCode?: BusinessErrorCodes,
  ) {
    try {
      const jsonResponse = JSON.parse(this.message);
      this.payload = jsonResponse;
      this.businessErrorCode = jsonResponse?.businessErrorCode;
    } catch {} // Fine, can't parse response as JSON, will assume plain text
  }
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Base type for all requests.
 *
 * It separates the URL in two components: `baseUrl` and `path` since we usually have a configuration
 * for API base URLs, and then we attach paths to it. It is always possible to provide the full URL
 * as `baseUrl` and skip `path`.
 */
export type BaseRequest = {
  baseUrl: string;
  path?: string;
  headers?: Record<string, string>;
};

export type GetRequest = BaseRequest & {
  queryParams?: Record<string, string>;
};

export type PostRequest<RequestType> = BaseRequest & {
  body?: RequestType;
};

export type PutRequest<RequestType> = PostRequest<RequestType>;

export type DeleteRequest = BaseRequest;

/**
 * Performs an HTTP request for a JSON resource. It is there for added flexibility in certain cases
 * but usually the `get`/`post`/`put`/`delete` methods here should be cleaner to use.
 *
 * @param baseUrl Base URL
 * @param path Path to attach to the base URL, if any.
 * @param method HTTP method to use for the request.
 * @param queryParams Query params; in theory only for `GET`.
 * @param headers Extra headers to use for the request; useful for authorization for example. Required headers for
 * JSON (such as `Content-Type` or `Accept`) are already handled within.
 * @param body HTTP payload for the request; in theory only for `POST`, `PUT`.
 */
export const fetchJson = async <RequestType, ResponseType>({
  baseUrl,
  path,
  method,
  queryParams,
  headers,
  body,
}: {
  baseUrl: string;
  path?: string;
  method: HttpMethod;
  queryParams?: Record<string, string>;
  headers?: Record<string, string>;
  body?: RequestType;
}): Promise<ResponseType> => {
  const url = buildUrl(baseUrl, path, queryParams);
  const request: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      // TODO change when implementing PATCH
      Accept: 'application/json',
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  };

  const response = await fetch(url, request);

  // TODO aquí hay un cambio con respecto a client-commons porque si no hay cabecera Number(null) es 0
  if (
    response.status === 204 ||
    (response.headers.get('content-length') != null && Number(response.headers.get('content-length')) === 0)
  ) {
    return undefined as ResponseType;
  } else if (response.status >= 200 && response.status < 300 && response.ok) {
    return response.json();
  } else {
    throw new HttpError(response.status, await response.text());
  }
};

export const buildUrl = (baseUrl: string, path?: string, queryParams?: Record<string, string>) => {
  let url = baseUrl;
  if (!baseUrl.endsWith('/') && path !== undefined) {
    url += '/';
  }
  url += path || '';
  if (queryParams) {
    url += `?${new URLSearchParams(queryParams).toString()}`;
  }
  return url;
};
